import instance from "lib/axiosInterceptor";
import {
  FetchedBookings,
  BookingsSetUpPayload,
  ConfirmBookingPayload,
  InviteToSpeakPayload,
  SpeakingInvitation,
  SpeakingInvitationStatus,
  GetSpeakingInvitationsParams,
  SpeakingInvitationsResponse,
  SendCorrespondence,
  GetCorrespondence,
  RespondToInvitation,
} from "interfaces";

/**
 * GET REQUESTS
 */

// Get all booking calendars
export async function getBookingCalendarsService(): Promise<FetchedBookings[]> {
  const response = await instance.get<FetchedBookings[]>("/Bookings/Calendar");
  const sortedBookings = response.data.sort(
    (a, b) =>
      new Date(b.dateModified).getTime() - new Date(a.dateModified).getTime(),
  );
  return sortedBookings;
}

/**
 * POST REQUESTS
 */

// Create a new booking calendar
export async function createBookingCalendarService(
  bookingData: BookingsSetUpPayload,
): Promise<string> {
  const response = await instance.post<string>(
    "/Bookings/Calendar",
    bookingData,
  );
  return response.data;
}

// Confirm a booking
export async function confirmBookingService(
  bookingId: string,
  payload: ConfirmBookingPayload,
): Promise<void> {
  await instance.post(`/Bookings/confirm/${bookingId}`, payload);
}

// cancel a booking slot
export async function cancelBookingSlotService(slotId: string): Promise<void> {
  await instance.put(`/Bookings/cancel/${slotId}`);
}

// Send meeting reminder
export async function sendMeetingReminderService(
  slotId: string,
): Promise<void> {
  await instance.post(`/Bookings/SendReminder/${slotId}`);
}

// Invite speaker to speak
export async function inviteToSpeakService(
  speakerId: string,
  payload: InviteToSpeakPayload,
): Promise<void> {
  await instance.post(`/Bookings/InviteToSpeak/${speakerId}`, payload);
}

export const getSpeakingInvitationService = async (
  params: GetSpeakingInvitationsParams = {},
): Promise<SpeakingInvitationsResponse> => {
  const result = await instance.get(`/Bookings/Invitations`, {
    params: {
      Page: params.page,
      PageLimit: params.pageLimit,
      Query: params.query,
      StartDate: params.startDate,
      EndDate: params.endDate,
      Status: params.status,
      SortDirection: params.sortDirection,
      Range: 5,
    },
  });

  const payload = result.data?.data ?? result.data;
  const listData = payload?.items ?? payload?.data ?? payload;
  const items: SpeakingInvitation[] = Array.isArray(listData)
    ? listData.map((item: SpeakingInvitation & { status: number | string }) => ({
        ...item,
        status: mapInvitationStatus(item.status),
      }))
    : [];

  return {
    items,
    pagination: payload?.pagination,
  };
};

function mapInvitationStatus(status: number | string): SpeakingInvitationStatus {
  if (status === 0 || status === "0" || status === "Pending") {
    return SpeakingInvitationStatus.Pending;
  }

  if (status === 1 || status === "1" || status === "Accepted") {
    return SpeakingInvitationStatus.Accepted;
  }

  if (status === 2 || status === "2" || status === "Declined") {
    return SpeakingInvitationStatus.Declined;
  }

  return SpeakingInvitationStatus.Pending;
}

export const getSpeakingCorrespondenceService = async (
  id: string,
): Promise<GetCorrespondence[]> => {
  const result = await instance.get(
    `/Bookings/Invitation/Correspondence/${id}`,
  );
  return result.data;
};

export const sendCorrespondenceService = async (
  payload: SendCorrespondence,
) => {
  const result = await instance.post(
    `/Bookings/Invitation/Correspondence/${payload.id}`,
    { message: payload.message },
  );
  return result.data.data;
};

export const respondToInvitationService = async (
  payload: RespondToInvitation,
) => {
  const result = await instance.put(
    `/Bookings/Invitation/Accept/${payload.id}?accept=${payload.accept}`,
  );
  return result.data;
};
